import { Handle, Position, useNodeConnections } from '@xyflow/react'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { useState, useEffect } from 'react'
import { CustomAssetManager } from '../../lib/CustomAssetManager.js'
import { selectedBorder, activeBorder } from './selectedBorder.js'

// Clip list is sourced live from CustomAssetManager (the single source of
// truth for the character's animation assets). Subscribing to it means an
// upload or an out-of-band sync + Refresh shows up in the dropdown without a
// page reload — no separate, never-invalidated manifest cache to drift.
function getClipsFromCAM(graphType) {
  const kind = graphType === 'face' ? 'face' : 'body'
  const assets = CustomAssetManager.listAnimationAssets().filter(a => a.kind === kind)
  return {
    names: assets.map(a => a.name),
    additive: new Set(assets.filter(a => a.additive).map(a => a.name)),
  }
}

function getCustomClips(graphType) {
  const gestures = CustomAssetManager.listGestures()
  const prefabs  = CustomAssetManager.listPrefabs()
  if (graphType === 'body') {
    return [
      ...gestures.filter(g => g.body_glb_url).map(g => ({ name: g.name, label: `${g.name}  [gesture]` })),
      ...prefabs.filter(p => p.body_glb_url).map(p => ({ name: p.name, label: `${p.name}  [prefab]` })),
    ]
  }
  // face graph — clips are registered with _face suffix
  return [
    ...gestures.filter(g => g.face_json_url).map(g => ({ name: `${g.name}_face`, label: `${g.name}_face  [gesture]` })),
    ...prefabs.filter(p => p.face_json_url).map(p => ({ name: `${p.name}_face`, label: `${p.name}_face  [prefab]` })),
  ]
}

export function PlayClipNode({ id, data, selected }) {
  const weight   = data._debugWeight ?? 0
  const progress = data._debugAnimRatio ?? 0   // 0..1 playback position within the clip
  const playing  = weight > 0.001
  const graphType = data._graphType ?? 'body'
  const [{ names: clips, additive: additiveSet }, setClipData] = useState(() => getClipsFromCAM(graphType))
  const [customClips, setCustomClips] = useState(() => getCustomClips(graphType))

  useEffect(() => {
    const update = () => {
      setClipData(getClipsFromCAM(graphType))
      setCustomClips(getCustomClips(graphType))
    }
    update()
    return CustomAssetManager.subscribe(update)
  }, [graphType])

  const clipName   = data.clipName ?? ''
  // A wired Speed pin (Get Variable, Random, …) overrides the typed value at
  // runtime (GraphEvaluator), so the field greys out to say it is not in use.
  const speedWired = useNodeConnections({ id, handleType: 'target', handleId: 'speed-in' }).length > 0
  const isAdditive = additiveSet.has(clipName) || !!window.__ADDITIVE_CLIP_NAMES__?.has(clipName)

  // ENG-524: a fresh node inherits the asset's `loopable` (Unreal's behavior,
  // where a freshly-placed Sequence Player inherits the asset's bLoop) the FIRST
  // time a clip is assigned. Once a clip is set, changing it preserves the user's
  // manual Loop choice — switching the animation must not silently clear Loop.
  const handleClipChange = (name) => {
    const hadClip = !!clipName  // was a clip already chosen on this node?
    data.onChange?.('clipName', name)
    if (name && name !== clipName && !hadClip) {
      const base = graphType === 'face' && name.endsWith('_face') ? name.slice(0, -5) : name
      const row = CustomAssetManager.listAnimationAssets?.().find(a => a.name === name || a.name === base)
      if (row) data.onChange?.('loop', !!row.loopable)
    }
  }

  // Additive clips get a green palette (Unreal convention: additive sequence
  // players are green). Non-additive keep the red/brown PLAY CLIP palette.
  // The "playing" border (#60d060) still wins in both cases.
  const palette = isAdditive
    ? { bg: selected ? '#243a26' : '#172b19', borderBase: '#3a8a44', title: '#8fe09a' }
    : { bg: selected ? '#3a2f2f' : '#2a1f1f', borderBase: '#7a4030', title: '#e8a080' }

  return (
    <div style={{
      background: palette.bg,
      border: `2px solid ${selected ? selectedBorder(palette.borderBase) : playing ? activeBorder(palette.borderBase) : palette.borderBase}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 160,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
      boxShadow: 'none',
    }}>
      {/* Node title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>🎬</span>
        <span style={{ color: palette.title, fontWeight: 'bold', fontSize: 11 }}>PLAY CLIP</span>
        {isAdditive && <span style={{ color: '#6080ff', fontSize: 8, background: '#1a1a3a', border: '1px solid #3040a0', borderRadius: 3, padding: '1px 4px', letterSpacing: 0.5 }}>ADDITIVE</span>}
        {playing && <span style={{ color: '#60d060', fontSize: 9, marginLeft: 'auto' }}>▶ {(weight * 100).toFixed(0)}%</span>}
      </div>

      {/* Clip name */}
      {(clips.length > 0 || customClips.length > 0) ? (
        <select
          className={nodeControlClass()}
          value={clipName}
          onChange={e => handleClipChange(e.target.value)}
          onMouseDown={e => e.stopPropagation()}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: '#1a1a1a',
            color: '#eee',
            border: '1px solid #555',
            borderRadius: 3,
            padding: '3px 6px',
            fontSize: 11,
            fontFamily: 'var(--m-mono)',
            outline: 'none',
          }}
        >
          {!clipName && (
            <option value="" disabled>— select a clip —</option>
          )}
          {!clips.includes(clipName) && !customClips.some(c => c.name === clipName) && clipName && (
            <option value={clipName}>{clipName}</option>
          )}
          {clips.length > 0 && (
            <optgroup label="Animations">
              {clips.map(c => <option key={c} value={c}>{c}</option>)}
            </optgroup>
          )}
          {customClips.length > 0 && (
            <optgroup label="Uploaded">
              {customClips.map(c => <option key={c.name} value={c.name}>{c.label}</option>)}
            </optgroup>
          )}
        </select>
      ) : (
        <input className={nodeControlClass()}
          value={clipName}
          onChange={e => handleClipChange(e.target.value)}
          onMouseDown={e => e.stopPropagation()}
          placeholder="clip name"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: '#1a1a1a',
            color: '#eee',
            border: '1px solid #555',
            borderRadius: 3,
            padding: '3px 6px',
            fontSize: 11,
            fontFamily: 'var(--m-mono)',
            outline: 'none',
          }}
        />
      )}

      {/* Play speed — typed, or driven by the wired speed-in pin */}
      <div
        className="nodrag nowheel"
        style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}
        onPointerDown={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <Handle
          type="target"
          position={Position.Left}
          id="speed-in"
          title="Wire a number (Get Variable, Random, …) to drive the speed. A value that is not a positive number keeps the typed Speed."
          style={{ background: '#4ecf7e', width: 8, height: 8, left: -14, top: '50%', transform: 'translateY(-50%)' }}
        />
        <span style={{ color: speedWired ? '#4ecf7e' : '#a08070', fontSize: 10, minWidth: 36 }}>
          Speed{speedWired ? ' (pin)' : ''}
        </span>
        <input className={nodeControlClass()}
          type="number" min={0.1} max={4} step={0.1}
          disabled={speedWired}
          title={speedWired ? 'Driven by the wired Speed pin; used only if the pin gives no positive number' : undefined}
          value={data.playSpeed ?? 1}
          onChange={e => {
            const v = parseFloat(e.target.value)
            if (Number.isFinite(v) && v > 0) data.onChange?.('playSpeed', v)
          }}
          onPointerDown={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          onWheel={e => e.stopPropagation()}
          style={{
            flex: 1,
            boxSizing: 'border-box',
            background: '#1a1a1a',
            color: '#eee',
            border: '1px solid #555',
            borderRadius: 3,
            padding: '2px 6px',
            fontSize: 11,
            fontFamily: 'var(--m-mono)',
            outline: 'none',
            textAlign: 'right',
            opacity: speedWired ? 0.45 : 1,
          }}
        />
      </div>

      {/* Loop toggle (ENG-524). Default true when unset. */}
      <label
        className="nodrag nowheel"
        style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
        onPointerDown={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <input
          className={nodeControlClass()}
          type="checkbox"
          checked={data.loop === true}
          onChange={e => data.onChange?.('loop', e.target.checked)}
          onPointerDown={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          style={{ accentColor: '#60d060', cursor: 'pointer' }}
        />
        <span style={{ color: '#a08070', fontSize: 10 }}>Loop</span>
        {data.loop !== true && (
          <span style={{ color: '#8088a0', fontSize: 9, marginLeft: 'auto' }}>holds last frame</span>
        )}
      </label>

      {/* Playback progress bar (0..1 of clip duration). Resets each loop. */}
      <div style={{ marginTop: 8, height: 3, background: '#2a2a2a', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
          height: '100%',
          background: '#60d060',
          // No CSS transition — progress is updated every frame; an interpolation
          // would lag behind and smear out the wrap point we use as a visual cue.
        }} />
      </div>

      {/* Output handle — animation pose */}
      <Handle
        type="source"
        position={Position.Right}
        id="anim"
        style={{ background: playing ? '#60d060' : '#e07040', width: 10, height: 10 }}
      />
    </div>
  )
}
